import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, TrendingUp, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, FileText, Clock, RotateCcw } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import { generateCommissionPDF } from '../utils/pdfGenerator';
import StyledDropdown from './StyledDropdown';
import PdfPreviewModal from './PdfPreviewModal';

const MyCommissions = () => {
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedProducts, setExpandedProducts] = useState({});
  const [exporting, setExporting] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfDownloadName, setPdfDownloadName] = useState('commission-statement.pdf');

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfPreviewOpen(false);
  };

  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  // Fetch commission data
  const fetchCommissions = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('month', filterMonth);
      params.append('year', filterYear);

      const response = await fetch(
        `${getApiUrl()}/api/commissions/my-commissions?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json();

      if (result.success) {
        setCommissionData(result.data);
      } else {
        setError(result.message || 'Failed to fetch commissions');
      }
    } catch (err) {
      console.error('Fetch commissions error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [filterMonth, filterYear]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  // Export PDF handler
  const handleExportPDF = async () => {
    if (!commissionData) return;
    
    setExporting(true);
    try {
      const monthName = months.find(m => m.value === filterMonth)?.label || '';
      const period = `${monthName} ${filterYear}`;
      
      // Use user info from API response (more accurate than local storage)
      const userInfo = {
        name: commissionData?.user?.name || 'N/A',
        accountId: commissionData?.user?.accountId || 'N/A',
        code: commissionData?.user?.accountId || 'N/A',
        role: commissionData?.user?.role || 'N/A',
        group: commissionData?.user?.group || 'N/A',
        reportsTo: commissionData?.user?.reportsTo || 'N/A',
      };
      
      const url = await generateCommissionPDF(commissionData, userInfo, period);
      setPdfDownloadName(`my-commission-${filterMonth}-${filterYear}.pdf`);
      setPdfPreviewUrl(url);
      setPdfPreviewOpen(true);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options dynamically
  // From 2020 (or system start year) to current year + 1 (for future planning)
  const currentYear = new Date().getFullYear();
  const startYear = 2020; // System start year
  const endYear = currentYear + 1; // Allow next year for planning
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);

  // Pagination for products
  const products = commissionData?.products || [];
  const {
    currentPage,
    paginatedItems: paginatedProducts,
    totalItems,
    handlePageChange,
  } = usePagination(products, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-dark-text">My Commissions</h1>
        <p className="text-gray-600 dark:text-dark-muted">View your commission earnings</p>
        </div>
        {commissionData && (
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FileText size={20} />
            {exporting ? 'Generating...' : 'Export PDF'}
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {/* Period Filter */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 mb-6 transition-colors duration-200">
        <div className="flex items-center gap-4">
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
            value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
            value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />
        </div>
      </div>

      {commissionData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                  <DollarSign size={24} className="text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Personal</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-dark-text">
                    {formatCurrency(commissionData.summary.totalPersonal)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <TrendingUp size={24} className="text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">TL Override</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-dark-text">
                    {formatCurrency(commissionData.summary.totalTLOverride)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <TrendingUp size={24} className="text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">GL Override</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-dark-text">
                    {formatCurrency(commissionData.summary.totalGLOverride)}
                  </div>
                </div>
              </div>
            </div>

            {/* Retroactive Commission Card */}
            {commissionData.retroactiveCommissions && commissionData.retroactiveCommissions.length > 0 && (
              <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-orange-200 dark:border-orange-900/50 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <RotateCcw size={24} className="text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-dark-muted">Retroactive</div>
                    <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                      {formatCurrency(commissionData.summary.totalRetroactive || 0)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-sm p-4 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <DollarSign size={24} />
                </div>
                <div>
                  <div className="text-sm text-primary-100">Total Eligible</div>
                  <div className="text-xl font-bold">
                    {formatCurrency(
                      (commissionData.summary.totalEligible || 0) + 
                      (commissionData.summary.totalRetroactive || 0)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Breakdown */}
          <div className="space-y-4">
            {products.length === 0 ? (
              <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-12 text-center">
                <DollarSign size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-medium text-gray-500 dark:text-dark-muted">No commissions for this period</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Commissions will appear here when sales are recorded
                </p>
              </div>
            ) : (
              paginatedProducts.map((product) => (
                <div
                  key={product.productId}
                  className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden"
                >
                  {/* Product Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
                    onClick={() => toggleProductExpand(product.productId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
                          <DollarSign size={20} className="text-gray-600 dark:text-gray-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-dark-text">{product.productName}</h3>
                          <div className="text-sm text-gray-500 dark:text-dark-muted space-y-0.5">
                            <p>Team Sales (This Month): {formatCurrency(product.totalTeamSales)}</p>
                            {product.cumulativePeriodSales !== undefined && product.campaignPeriod && (
                              <p className="flex items-center gap-1">
                                <Clock size={12} />
                                Campaign Total: {formatCurrency(product.cumulativePeriodSales)}
                                <span className="text-xs text-gray-400 ml-1">
                                  ({formatDate(product.campaignPeriod.startDate)} - {formatDate(product.campaignPeriod.endDate)})
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-500 dark:text-dark-muted">Eligible</div>
                          <div className="text-lg font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(product.totalEligible)}
                          </div>
                        </div>
                        {expandedProducts[product.productId] ? (
                          <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedProducts[product.productId] && (
                    <div className="border-t border-gray-200 p-4 space-y-4">
                      {/* Personal Commission */}
                      <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={18} className="text-primary-600 dark:text-primary-400" />
                            <span className="font-medium text-primary-800 dark:text-primary-300">Personal Commission</span>
                          </div>
                          <span className="font-bold text-primary-800 dark:text-primary-300">
                            {formatCurrency(product.personal.eligible)}
                          </span>
                        </div>
                        {product.personal.records.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {product.personal.records.map((record, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-sm text-primary-700 dark:text-primary-300"
                              >
                                <span>
                                  {formatDate(record.date)} - {formatCurrency(record.saleAmount)} ×{' '}
                                  {(record.rate * 100).toFixed(2)}%
                                </span>
                                <span>{formatCurrency(record.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* TL Override */}
                      {product.tlOverride.total > 0 && (
                        <div
                          className={`rounded-lg p-4 ${
                            product.tlOverride.thresholdMet ? 'bg-green-50' : 'bg-yellow-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {product.tlOverride.thresholdMet ? (
                                <CheckCircle size={18} className="text-green-600" />
                              ) : (
                                <AlertTriangle size={18} className="text-yellow-600" />
                              )}
                              <span
                                className={`font-medium ${
                                  product.tlOverride.thresholdMet
                                    ? 'text-green-800'
                                    : 'text-yellow-800'
                                }`}
                              >
                                TL Override
                              </span>
                            </div>
                            <div className="text-right">
                              <span
                                className={`font-bold ${
                                  product.tlOverride.thresholdMet
                                    ? 'text-green-800'
                                    : 'text-yellow-800'
                                }`}
                              >
                                {formatCurrency(product.tlOverride.eligible)}
                              </span>
                              {!product.tlOverride.thresholdMet && (
                                <div className="text-sm text-yellow-600">
                                  (Recorded: {formatCurrency(product.tlOverride.total)})
                                </div>
                              )}
                            </div>
                          </div>

                          {product.tlOverride.threshold && (
                            <div
                              className={`text-sm ${
                                product.tlOverride.thresholdMet
                                  ? 'text-green-700 dark:text-green-400'
                                  : 'text-yellow-700 dark:text-yellow-400'
                              }`}
                            >
                              {product.tlOverride.cumulativeSales !== undefined && (
                                <div>Cumulative Sales: {formatCurrency(product.tlOverride.cumulativeSales)}</div>
                              )}
                              {product.tlOverride.unattachedSales !== undefined && (
                                <div>Unattached Sales: {formatCurrency(product.tlOverride.unattachedSales)}</div>
                              )}
                              <div>
                                Threshold: {formatCurrency(product.tlOverride.threshold)}
                                {product.tlOverride.thresholdMet && product.tlOverride.thresholdMetDate && (
                                  <span className="ml-2 text-green-600 dark:text-green-400">
                                    ✓ Met on {formatDate(product.tlOverride.thresholdMetDate)}
                                  </span>
                                )}
                                {!product.tlOverride.thresholdMet && (
                                  <span className="ml-2">
                                    (Need {formatCurrency(product.tlOverride.shortfall)} more)
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {product.tlOverride.records.filter(r => !r.isRetroactive).length > 0 && (
                            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                              {product.tlOverride.records
                                .filter(r => !r.isRetroactive)
                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                .map((record, idx) => (
                                <div
                                  key={idx}
                                  className={`flex justify-between text-sm ${
                                    product.tlOverride.thresholdMet
                                      ? 'text-green-700 dark:text-green-400'
                                      : 'text-yellow-700 dark:text-yellow-400'
                                  }`}
                                >
                                  <span>
                                    {formatDate(record.date)} - {record.salesPerson} -{' '}
                                    {formatCurrency(record.saleAmount)} × {(record.rate * 100).toFixed(2)}%
                                  </span>
                                  <span>{formatCurrency(record.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* GL Override */}
                      {product.glOverride.total > 0 && (
                        <div
                          className={`rounded-lg p-4 ${
                            product.glOverride.thresholdMet ? 'bg-purple-50' : 'bg-yellow-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {product.glOverride.thresholdMet ? (
                                <CheckCircle size={18} className="text-purple-600" />
                              ) : (
                                <AlertTriangle size={18} className="text-yellow-600" />
                              )}
                              <span
                                className={`font-medium ${
                                  product.glOverride.thresholdMet
                                    ? 'text-purple-800'
                                    : 'text-yellow-800'
                                }`}
                              >
                                GL Override
                              </span>
                            </div>
                            <div className="text-right">
                              <span
                                className={`font-bold ${
                                  product.glOverride.thresholdMet
                                    ? 'text-purple-800'
                                    : 'text-yellow-800'
                                }`}
                              >
                                {formatCurrency(product.glOverride.eligible)}
                              </span>
                              {!product.glOverride.thresholdMet && (
                                <div className="text-sm text-yellow-600">
                                  (Recorded: {formatCurrency(product.glOverride.total)})
                                </div>
                              )}
                            </div>
                          </div>

                          {product.glOverride.threshold && (
                            <div
                              className={`text-sm ${
                                product.glOverride.thresholdMet
                                  ? 'text-purple-700 dark:text-purple-400'
                                  : 'text-yellow-700 dark:text-yellow-400'
                              }`}
                            >
                              {product.glOverride.cumulativeSales !== undefined && (
                                <div>Cumulative Sales: {formatCurrency(product.glOverride.cumulativeSales)}</div>
                              )}
                              <div>
                                Threshold: {formatCurrency(product.glOverride.threshold)}
                                {product.glOverride.thresholdMet && product.glOverride.thresholdMetDate && (
                                  <span className="ml-2 text-purple-600 dark:text-purple-400">
                                    ✓ Met on {formatDate(product.glOverride.thresholdMetDate)}
                                  </span>
                                )}
                                {!product.glOverride.thresholdMet && (
                                  <span className="ml-2">
                                    (Need {formatCurrency(product.glOverride.shortfall)} more)
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {product.glOverride.records.filter(r => !r.isRetroactive).length > 0 && (
                            <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                              {product.glOverride.records
                                .filter(r => !r.isRetroactive)
                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                .map((record, idx) => (
                                <div
                                  key={idx}
                                  className={`flex justify-between text-sm ${
                                    product.glOverride.thresholdMet
                                      ? 'text-purple-700 dark:text-purple-400'
                                      : 'text-yellow-700 dark:text-yellow-400'
                                  }`}
                                >
                                  <span>
                                    {formatDate(record.date)} - {record.salesPerson} -{' '}
                                    {formatCurrency(record.saleAmount)} × {(record.rate * 100).toFixed(2)}%
                                  </span>
                                  <span>{formatCurrency(record.amount)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Retroactive Commission Section (like admin side) */}
                      {(() => {
                        const retroRecords = [
                          ...product.tlOverride.records.filter(r => r.isRetroactive),
                          ...product.glOverride.records.filter(r => r.isRetroactive)
                        ];
                        if (retroRecords.length === 0) return null;
                        
                        const totalRetro = retroRecords.reduce((sum, r) => sum + r.amount, 0);
                        
                        return (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-2 flex items-center gap-2">
                              <RotateCcw className="w-4 h-4" />
                              Retroactive Commission (From Prior Period Sales)
                            </p>
                            <p className="text-xs text-gray-500 dark:text-dark-muted mb-2">
                              These commissions from previous months became eligible this month when threshold was met.
                            </p>
                            <div className="space-y-1 text-sm">
                              {retroRecords
                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                .map((record, idx) => {
                                  const commType = product.tlOverride.records.includes(record) ? 'TL Override' : 'GL Override';
                                  const saleDate = formatDate(record.date);
                                  
                                  return (
                                    <div 
                                      key={`retro-${idx}`}
                                      className="flex items-center justify-between py-1 px-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/30"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          commType === 'TL Override'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                        }`}>
                                          {commType}
                                        </span>
                                        <span className="text-gray-600 dark:text-dark-muted">
                                          {product.productName}
                                        </span>
                                        <span className="text-gray-400 dark:text-dark-muted">
                                          ({(record.rate * 100).toFixed(2)}%)
                                        </span>
                                        <span className="text-xs text-orange-600 dark:text-orange-400">
                                          Sale: {saleDate}
                                        </span>
                                      </div>
                                      <span className="font-medium text-orange-600 dark:text-orange-400">
                                        {formatCurrency(record.amount)}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))
            )}
            
            {/* Pagination */}
            {products.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={10}
                onPageChange={handlePageChange}
              />
            )}
          </div>

        </>
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        pdfUrl={pdfPreviewUrl}
        title="Commission report preview"
        downloadFilename={pdfDownloadName}
        onClose={closePdfPreview}
      />
    </div>
  );
};

export default MyCommissions;

